import React, { useEffect, useRef } from "react";
import { useReducedMotion } from "framer-motion";
import Container from "../layout/Container";
import Reveal from "../motion/Reveal";
import WeekBoard from "../mockups/WeekBoard";
import Studio from "./Studio";
import { StampCalendar, StampCoin, StampStory } from "../characters/Decor";
import { useI18n } from "../../i18n/I18nProvider";

const PinCard = ({ id, pinClass = "", faceClass, children }) => {
  const reduced = useReducedMotion();
  const pinRef = useRef(null);
  const faceRef = useRef(null);

  useEffect(() => {
    const pin = pinRef.current;
    const face = faceRef.current;
    if (!pin || !face) return undefined;

    let frame = 0;
    const desktop = window.matchMedia("(min-width: 1024px)");

    const measure = () => {
      frame = 0;
      if (reduced || !desktop.matches) {
        face.style.transform = "";
        return;
      }

      const rect = pin.getBoundingClientRect();
      const next = pin.nextElementSibling;
      let progress = 0;

      if (next) {
        const cover = rect.bottom - next.getBoundingClientRect().top;
        progress = Math.min(1, Math.max(0, cover / Math.max(rect.height * 0.92, 1)));
      } else {
        const stickyTop = parseFloat(getComputedStyle(pin).top) || 88;
        const pushed = stickyTop - rect.top;
        if (pushed > 1) {
          progress = Math.min(1, pushed / Math.max(rect.height * 0.72, 1));
        }
      }

      if (progress <= 0.01) {
        face.style.transform = "";
        return;
      }

      face.style.transform = `scale(${(1 - progress * 0.1).toFixed(4)})`;
    };

    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(measure);
    };

    measure();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [reduced]);

  return (
    <article id={id} ref={pinRef} className={`desk-pin scroll-mt-24 ${pinClass}`}>
      <div ref={faceRef} className={`desk-pin-face ${faceClass}`}>
        {children}
      </div>
    </article>
  );
};

const Story = ({ id, pinClass = "", faceClass, scene, href, plate, Stamp, children }) => {
  const { t } = useI18n();
  const points = t(`rows.${scene}.points`);

  return (
    <PinCard id={id} pinClass={pinClass} faceClass={faceClass}>
      <div className="grid items-start gap-8 lg:grid-cols-2">
        <div>
          <div className="flex items-center gap-3">
            <Stamp className="h-9 w-9 shrink-0" />
            <p className="text-body font-medium text-accent">{t(`rows.${scene}.kicker`)}</p>
          </div>
          <h3 className="mt-2 max-w-xl text-heading-sm font-semibold text-ink md:text-heading">
            {t(`rows.${scene}.title`)}
          </h3>
          <p className="mt-3 max-w-xl text-body text-mid-gray">{t(`rows.${scene}.body`)}</p>
          <ul className="mt-6 flex flex-col gap-4 p-0">
            {points.map((point) => (
              <li key={point.title} className="list-none">
                <p className="text-body font-semibold text-ink">{point.title}</p>
                <p className="mt-1 text-body text-mid-gray">{point.body}</p>
              </li>
            ))}
          </ul>
          <a
            href={href}
            className="mt-6 inline-flex min-h-11 items-center text-body font-semibold text-ink no-underline"
          >
            {t(scene === "desk" ? "desks.linkAdvertiser" : "desks.linkInfluencer")}
          </a>
        </div>
        <div className="relative min-w-0">
          <div
            className={`pointer-events-none absolute inset-x-2 top-4 bottom-4 rounded-stage ${plate}`}
            aria-hidden="true"
          />
          <div className="relative">{children}</div>
        </div>
      </div>
      <p className="mt-8 rounded-card bg-lilac px-4 py-3 text-body text-[#241c3e]">
        {t(`rows.${scene}.outcome`)}
      </p>
    </PinCard>
  );
};

const DeskPreview = () => {
  const { t } = useI18n();
  const nav = t("rows.desk.nav");

  return (
    <div className="overflow-hidden rounded-card border border-hairline bg-canvas">
      <div className="flex flex-col sm:grid sm:grid-cols-[9rem_minmax(0,1fr)]">
        <ul className="m-0 flex gap-1 overflow-x-auto border-b border-hairline bg-paper p-2 sm:flex-col sm:overflow-visible sm:border-b-0 sm:border-r sm:p-3">
          {nav.map((item, index) => (
            <li
              key={item}
              className={`list-none shrink-0 rounded-nested px-2 py-1.5 text-caption sm:py-2 ${
                index === 0 ? "bg-lilac font-semibold text-[#241c3e]" : "text-mid-gray"
              }`}
            >
              {item}
            </li>
          ))}
        </ul>
        <div className="flex flex-col gap-3 p-4">
          <div className="rounded-nested border border-hairline bg-paper p-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-body font-semibold text-ink">{t("rows.desk.campaign")}</p>
                <p className="mt-1 text-caption text-mid-gray">{t("rows.desk.publisher")}</p>
              </div>
              <span className="rounded-pill bg-lilac px-2 py-1 text-caption text-[#241c3e]">
                {t("rows.desk.status")}
              </span>
            </div>
          </div>
          <div className="rounded-nested border border-hairline bg-paper p-3">
            <p className="text-caption font-medium text-accent">{t("rows.desk.report")}</p>
            <p className="mt-1 text-body text-ink">{t("rows.desk.reportBody")}</p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-nested bg-paper p-3">
              <p className="text-caption text-mid-gray">{t("rows.desk.credit")}</p>
              <p className="mt-1 text-body font-semibold text-ink">{t("rows.desk.creditValue")}</p>
            </div>
            <div className="rounded-nested bg-paper p-3">
              <p className="text-caption text-mid-gray">{t("rows.desk.transaction")}</p>
              <p className="mt-1 text-body text-ink">{t("rows.desk.transactionBody")}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Why = () => {
  const { t } = useI18n();
  const stackRef = useRef(null);

  useEffect(() => {
    const stack = stackRef.current;
    if (!stack) return undefined;

    let frame = 0;
    const fit = () => {
      const faces = [...stack.querySelectorAll(".desk-pin-face")];
      const desktop = window.matchMedia("(min-width: 1024px)").matches;
      if (!desktop || faces.length === 0) {
        faces.forEach((face) => {
          face.style.minHeight = "";
        });
        return;
      }
      const natural = faces.map((face) => {
        const previous = face.style.minHeight;
        face.style.minHeight = "0px";
        const height = face.scrollHeight;
        face.style.minHeight = previous;
        return height;
      });
      const tallest = Math.max(...natural);
      faces.forEach((face) => {
        face.style.minHeight = `${tallest}px`;
      });
    };

    const schedule = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(fit);
    };

    schedule();
    window.addEventListener("resize", schedule);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("resize", schedule);
    };
  }, [t]);

  return (
    <section className="py-8 md:py-16">
      <Container>
        <Reveal>
          <h2 className="max-w-2xl text-heading font-bold tracking-heading text-ink md:text-heading-lg">
            {t("rows.title")}
          </h2>
          <p className="mt-4 max-w-2xl text-body text-mid-gray">{t("rows.body")}</p>
        </Reveal>
        <div ref={stackRef} className="desk-stack mt-10 flex flex-col gap-6">
          <Story
            id="calendar"
            scene="calendar"
            href="https://me.influ8.app"
            plate="plate-sky"
            Stamp={StampCalendar}
            faceClass="rounded-stage border border-hairline bg-paper p-4 shadow-card md:p-8"
          >
            <WeekBoard story />
          </Story>
          <Story
            id="rates"
            scene="rate"
            pinClass="desk-pin-2"
            href="https://me.influ8.app"
            plate="plate-sun"
            Stamp={StampCoin}
            faceClass="rounded-stage border border-hairline bg-canvas p-4 shadow-lift md:p-8"
          >
            <Studio embedded />
          </Story>
          <Story
            id="desks"
            scene="desk"
            pinClass="desk-pin-3"
            href="https://dashboard.influ8.app"
            plate="plate-mint"
            Stamp={StampStory}
            faceClass="rounded-stage border border-hairline bg-paper p-4 shadow-card md:p-8"
          >
            <DeskPreview />
          </Story>
        </div>
      </Container>
    </section>
  );
};

export default Why;
